import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Row,
  Col,
  Table,
  CardBody,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input
} from 'reactstrap'
import { useSelector } from 'react-redux'
import { API_URL } from './utils/Constants/URLS'
import PackList from './PackList' // adjust path if needed

const ViewDI = () => {
  const { soid, sonumber } = useParams()
  const [diList, setDiList] = useState([])
  const [loading, setLoading] = useState(true)
  const [openDI, setOpenDI] = useState(null)
  const [declineModalOpen, setDeclineModalOpen] = useState(false)
  const [declineReason, setDeclineReason] = useState('')
  const [activeDiId, setActiveDiId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const userDetails = useSelector(store => store.user)

  const [editingDiId, setEditingDiId] = useState(null)
  const [editedLineItems, setEditedLineItems] = useState([])
  const [editedStore, setEditedStore] = useState(null)

  const [showPackListDiId, setShowPackListDiId] = useState(null)

  // new: soft-delete (status -> 'deleted')
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteDiId, setDeleteDiId] = useState(null)

  const token = localStorage.getItem('token')

  useEffect(() => {
    const fetchDI = async () => {
      try {
        setLoading(true)
        const myHeaders = new Headers()
        myHeaders.append('Content-Type', 'application/json')
        myHeaders.append('Authorization', `Bearer ${token}`)
        const requestOptions = {
          method: 'GET',
          headers: myHeaders,
          redirect: 'follow'
        }
        const response = await fetch(
          `${API_URL}delivery-instructions/sales-order/${sonumber}`,
          requestOptions
        )
        if (!response.ok)
          throw new Error(`Error ${response.status}: ${response.statusText}`)
        const result = await response.json()
        const list = Array.isArray(result?.data) ? result.data : []
        const sorted = list.sort((a, b) => {
          if (a._id === soid || a.salesOrder?._id === soid) return -1
          if (b._id === soid || b.salesOrder?._id === soid) return 1
          return 0
        })
        setDiList(sorted)
      } catch (error) {
        console.error('Error fetching Delivery Instruction:', error)
        setDiList([])
      } finally {
        setLoading(false)
      }
    }
    if (sonumber) fetchDI()
  }, [soid, sonumber, token])

  const toggleDI = id => setOpenDI(prev => (prev === id ? null : id))

  const formatDate = date =>
    date
      ? new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
      : '-'

  const getModelName = line =>
    line?.model?.modelName ??
    line?.modelName ??
    line?.name ??
    (typeof line?.model === 'string' ? line.model : '-') ??
    '-'

  const getPartCode = line =>
    line?.model?.partCode ?? line?.partCode ?? line?.part_code ?? '-'

  /**
   * changeDIStatus
   * - NOTE: store will NOT be sent in payload for approval anymore
   * - includes reasonForRejection if status === 'rejected'
   */
  const changeDIStatus = async (diId, status, reason) => {
    if (!diId) return
    if (status === 'rejected' && (!reason || reason.trim().length < 3)) {
      alert('Please provide a reason for rejection (min 3 characters).')
      return
    }
    try {
      setSubmitting(true)
      const myHeaders = new Headers()
      myHeaders.append('Content-Type', 'application/json')
      myHeaders.append(
        'Authorization',
        `Bearer ${localStorage.getItem('token')}`
      )
      const body = { status }
      if (status === 'rejected') body.reasonForRejection = reason.trim()
      // IMPORTANT: Do NOT include `store` when approving per request.

      const requestOptions = {
        method: 'PATCH',
        headers: myHeaders,
        body: JSON.stringify(body),
        redirect: 'follow'
      }
      const res = await fetch(
        `${API_URL}delivery-instructions/${diId}`,
        requestOptions
      )
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Status ${res.status}`)
      }
      const result = await res.json()
      setDiList(prev =>
        prev.map(d =>
          String(d._id) === String(diId) ? { ...d, ...result } : d
        )
      )

      if (status === 'rejected') {
        setDeclineModalOpen(false)
        setActiveDiId(null)
        setDeclineReason('')
      }
      alert(
        `Delivery Instruction ${status === 'approved' ? 'approved' : 'rejected'
        }.`
      )
      window.location.reload()
    } catch (err) {
      console.error('Change status error', err)
      alert('Failed to change DI status.')
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * restoreSOQuantitiesAndRejectDI
   *
   * Changed behaviour per request:
   * - Instead of restoring "quantity", we should restore diQuantity in the Sales Order.
   * - For each DI line, subtract the DI quantity from the corresponding SO line's diQuantity.
   *   e.g. DI qty = 12, so.diQuantity = 72 -> after rejection so.diQuantity = 60
   */
  const restoreSOQuantitiesAndRejectDI = async (diId, reason) => {
    if (!diId) return
    if (!soid) {
      alert('Sales Order id missing; cannot restore quantities.')
      return
    }
    if (!reason || reason.trim().length < 3) {
      alert('Please provide a reason for rejection (min 3 characters).')
      return
    }
    try {
      setSubmitting(true)
      const di = diList.find(d => String(d._id) === String(diId))
      const diLineItems = Array.isArray(di?.lineItems) ? di.lineItems : []
      const token = localStorage.getItem('token')

      // Fetch current SO
      const soRes = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })
      if (!soRes.ok) {
        const txt = await soRes.text()
        throw new Error(`Failed to fetch SO: ${txt || soRes.status}`)
      }
      const soJson = await soRes.json()
      const soData = soJson?.data || {}
      const soLineItems = Array.isArray(soData.lineItems)
        ? soData.lineItems
        : []

      // Build maps from DI lines to qty to subtract from diQuantity
      const diById = new Map()
      const diByModel = new Map()
      diLineItems.forEach(dli => {
        const keyId = dli._id ? String(dli._id) : null
        const modelId =
          dli?.model?._id ?? (typeof dli.model === 'string' ? dli.model : null)
        const qty = Number(dli.quantity ?? dli.qty ?? 0)
        if (keyId) diById.set(keyId, (diById.get(keyId) || 0) + qty)
        if (modelId)
          diByModel.set(
            String(modelId),
            (diByModel.get(String(modelId)) || 0) + qty
          )
      })

      // Create updated SO line items: subtract DI qty from diQuantity (floor at 0)
      const updatedSoLineItems = soLineItems.map(orig => {
        const origId = orig?._id ? String(orig._id) : null
        const origModelId =
          orig?.model?._id ??
          (typeof orig.model === 'string' ? orig.model : null)

        let subtractQty = 0
        if (origId && diById.has(origId)) subtractQty += diById.get(origId)
        if (origModelId && diByModel.has(String(origModelId)))
          subtractQty += diByModel.get(String(origModelId))

        const currentDiQty = Number(orig.diQuantity ?? 0)
        const newDiQty = Math.max(
          0,
          Number(currentDiQty) - Number(subtractQty || 0)
        )

        // Keep original quantity field unchanged
        return { ...orig, diQuantity: newDiQty }
      })

      const patchPayload = {
        lineItems: updatedSoLineItems,
        status: soData?.status ?? 'pending'
      }

      const soPatchRes = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(patchPayload)
      })

      const soPatchJson = await soPatchRes.json()
      if (!(soPatchRes.ok && soPatchJson && soPatchJson.error === false)) {
        throw new Error(soPatchJson?.message || 'Failed to update Sales Order')
      }

      // Now reject the DI (this will update DI status & reason)
      await changeDIStatus(diId, 'rejected', reason)

      alert('DI rejected and Sales Order diQuantity updated successfully.')
    } catch (err) {
      console.error('Error while restoring SO diQuantity and rejecting DI', err)
      alert(
        'Failed to restore SO diQuantity / reject DI. See console for details.'
      )
    } finally {
      setSubmitting(false)
      setDeclineModalOpen(false)
      setActiveDiId(null)
      setDeclineReason('')
    }
  }

  const openDeclineModal = diId => {
    setActiveDiId(diId)
    setDeclineReason('')
    setDeclineModalOpen(true)
  }

  const closeDeclineModal = () => {
    setDeclineModalOpen(false)
    setActiveDiId(null)
    setDeclineReason('')
  }

  // ---------- New: Cancel (soft-delete) handlers ----------
  const openDeleteModal = diId => {
    setDeleteDiId(diId)
    setDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setDeleteModalOpen(false)
    setDeleteDiId(null)
  }

  // mark DI status as 'deleted' via PATCH
  // mark DI status as 'deleted' via PATCH and restore SO diQuantity
  const markDIAsDeleted = async () => {
    if (!deleteDiId) return
    if (!soid) {
      alert('Sales Order id missing; cannot restore quantities.')
      return
    }
    try {
      setSubmitting(true)
      const token = localStorage.getItem('token')

      // Get the DI to be deleted
      const di = diList.find(d => String(d._id) === String(deleteDiId))
      const diLineItems = Array.isArray(di?.lineItems) ? di.lineItems : []

      // Fetch current SO
      const soRes = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })
      if (!soRes.ok) {
        const txt = await soRes.text()
        throw new Error(`Failed to fetch SO: ${txt || soRes.status}`)
      }
      const soJson = await soRes.json()
      const soData = soJson?.data || {}
      const soLineItems = Array.isArray(soData.lineItems)
        ? soData.lineItems
        : []

      // Build maps from DI lines to qty to subtract from diQuantity
      const diById = new Map()
      const diByModel = new Map()
      diLineItems.forEach(dli => {
        const keyId = dli._id ? String(dli._id) : null
        const modelId =
          dli?.model?._id ?? (typeof dli.model === 'string' ? dli.model : null)
        const qty = Number(dli.quantity ?? dli.qty ?? 0)
        if (keyId) diById.set(keyId, (diById.get(keyId) || 0) + qty)
        if (modelId)
          diByModel.set(
            String(modelId),
            (diByModel.get(String(modelId)) || 0) + qty
          )
      })

      // Create updated SO line items: subtract DI qty from diQuantity (floor at 0)
      const updatedSoLineItems = soLineItems.map(orig => {
        const origId = orig?._id ? String(orig._id) : null
        const origModelId =
          orig?.model?._id ??
          (typeof orig.model === 'string' ? orig.model : null)

        let subtractQty = 0
        if (origId && diById.has(origId)) subtractQty += diById.get(origId)
        if (origModelId && diByModel.has(String(origModelId)))
          subtractQty += diByModel.get(String(origModelId))

        const currentDiQty = Number(orig.diQuantity ?? 0)
        const newDiQty = Math.max(
          0,
          Number(currentDiQty) - Number(subtractQty || 0)
        )

        // Keep original quantity field unchanged
        return { ...orig, diQuantity: newDiQty }
      })

      // Update the Sales Order with restored diQuantity
      const patchPayload = {
        lineItems: updatedSoLineItems,
        status: soData?.status ?? 'pending'
      }

      const soPatchRes = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(patchPayload)
      })

      const soPatchJson = await soPatchRes.json()
      if (!(soPatchRes.ok && soPatchJson && soPatchJson.error === false)) {
        throw new Error(soPatchJson?.message || 'Failed to update Sales Order')
      }

      // Now mark the DI as deleted
      const myHeaders = new Headers()
      myHeaders.append('Content-Type', 'application/json')
      myHeaders.append('Authorization', `Bearer ${token}`)

      const body = { status: 'deleted' }

      const res = await fetch(`${API_URL}delivery-instructions/${deleteDiId}`, {
        method: 'PATCH',
        headers: myHeaders,
        body: JSON.stringify(body),
        redirect: 'follow'
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Status ${res.status}`)
      }

      const result = await res.json()
      // merge returned DI object (or fields) into local diList
      setDiList(prev =>
        prev.map(d =>
          String(d._id) === String(deleteDiId) ? { ...d, ...result } : d
        )
      )

      alert(
        'Delivery Instruction cancelled and Sales Order Quantity restored successfully.'
      )
      closeDeleteModal()
      window.location.reload()
    } catch (err) {
      console.error(
        'Error while cancelling DI and restoring SO diQuantity',
        err
      )
      alert(
        'Failed to cancel the Delivery Instruction. See console for details.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * Edit flow
   */
  const startEdit = di => {
    setEditingDiId(String(di._id))
    const normalized = Array.isArray(di.lineItems)
      ? di.lineItems.map(li => ({
        _id: li._id ?? null,
        model: li.model,
        quantity: Number(li.quantity ?? li.qty ?? 0)
      }))
      : []
    setEditedLineItems(normalized)
    setEditedStore(di?.store?._id ?? di?.store ?? null)
  }

  const cancelEdit = () => {
    setEditingDiId(null)
    setEditedLineItems([])
    setEditedStore(null)
  }

  const onEditQtyChange = (idx, value) => {
    const numeric = Number(value || 0)
    setEditedLineItems(prev => {
      const copy = prev.slice()
      copy[idx] = { ...copy[idx], quantity: numeric }
      return copy
    })
  }

  const saveEdit = async di => {
    if (!editingDiId) return
    try {
      setSubmitting(true)
      const token = localStorage.getItem('token')

      // Build maps of edited line quantities by _id and by model id
      const editedMapById = new Map()
      const editedMapByModel = new Map()
      editedLineItems.forEach(li => {
        const idKey = li._id ? String(li._id) : null
        const modelId =
          li?.model?._id ?? (typeof li.model === 'string' ? li.model : null)
        const qty = Number(li.quantity || 0)
        if (idKey)
          editedMapById.set(idKey, (editedMapById.get(idKey) || 0) + qty)
        if (modelId)
          editedMapByModel.set(
            String(modelId),
            (editedMapByModel.get(String(modelId)) || 0) + qty
          )
      })

      // Fetch current Sales Order
      const soRes = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })
      if (!soRes.ok) {
        const txt = await soRes.text()
        throw new Error(`Failed to fetch SO: ${txt || soRes.status}`)
      }
      const soJson = await soRes.json()
      const soData = soJson?.data || {}
      const soLineItems = Array.isArray(soData.lineItems)
        ? soData.lineItems
        : []

      // Determine whether this edit is a re-application after rejection.
      // If the DI status includes 'reject' we assume it was rejected and now re-applied.
      const isReapply = String(di?.status || '')
        .toLowerCase()
        .includes('reject')

      // NEW: when reapplying after rejection -> increment diQuantity (accumulate).
      // Otherwise keep existing behaviour (subtract quantities from `quantity`).
      const updatedSoLineItems = soLineItems.map(orig => {
        const origId = orig?._id ? String(orig._id) : null
        const origModelId =
          orig?.model?._id ??
          (typeof orig.model === 'string' ? orig.model : null)

        // Sum edited quantities that match this original line (by id or model)
        let changedQty = 0
        if (origId && editedMapById.has(origId))
          changedQty += editedMapById.get(origId)
        if (origModelId && editedMapByModel.has(String(origModelId)))
          changedQty += editedMapByModel.get(String(origModelId))

        const currentQty = Number(orig.quantity ?? 0)
        const currentDiQty = Number(orig.diQuantity ?? 0)

        if (isReapply) {
          // Add edited qty to diQuantity (accumulate), cap at total quantity
          const newDiQty = Math.min(
            currentQty,
            currentDiQty + Number(changedQty || 0)
          )
          return { ...orig, diQuantity: newDiQty }
        } else {
          // Original behaviour: subtract edited qty from available quantity
          const newQuantity = Math.max(
            0,
            Number(orig.quantity ?? 0) - Number(changedQty || 0)
          )
          return { ...orig, quantity: newQuantity }
        }
      })

      // Patch the SO accordingly
      const soPatchPayload = {
        lineItems: updatedSoLineItems,
        status: soData?.status ?? 'pending'
      }
      const soPatchRes = await fetch(`${API_URL}leads/sales-orders/${soid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(soPatchPayload)
      })
      const soPatchJson = await soPatchRes.json()
      if (!(soPatchRes.ok && soPatchJson && soPatchJson.error === false)) {
        throw new Error(soPatchJson?.message || 'Failed to update Sales Order')
      }

      // Prepare DI line items payload (same as before)
      const payloadLineItems = editedLineItems.map(li => {
        const modelId =
          li?.model?._id ?? (typeof li.model === 'string' ? li.model : null)
        return {
          model: modelId,
          quantity: Number(li.quantity || 0),
          _id: li._id ?? undefined
        }
      })

      const diStatusToSend = 'pending'
      const diBody = {
        lineItems: payloadLineItems,
        status: diStatusToSend
      }
      // executives cannot change store (store select hidden for executives)
      if (userDetails?.role === 'commercial_manager' && editedStore) {
        // include store when manager edited it
        diBody.store = editedStore
      }

      // Patch the DI
      const diRes = await fetch(
        `${API_URL}delivery-instructions/${editingDiId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(diBody)
        }
      )
      const diJson = await diRes.json()
      if (!(diRes.ok && diJson)) {
        throw new Error(diJson?.message || 'Failed to update DI')
      }

      // Update local list and UI
      setDiList(prev =>
        prev.map(d =>
          String(d._id) === String(editingDiId) ? { ...d, ...diJson } : d
        )
      )
      cancelEdit()
      alert('Delivery Instruction updated and submitted for approval.')
    } catch (err) {
      console.error('Error saving edit', err)
      alert('Failed to save changes to Delivery Instruction.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <p>Loading Delivery Instruction...</p>

  const filteredDiList = diList.filter(d => {
    const role = userDetails?.role
    const status = (d?.status || '').toLowerCase()

    if (role === 'commercial_executive') {
      return (
        status.includes('reject') ||
        status.includes('pending') ||
        status.includes('deleted')
      )
    }

    if (role === 'commercial_manager') {
      return (
        status.includes('pending') ||
        status.includes('approved') ||
        status.includes('deleted')
      )
    }

    return true
  })

  if (!filteredDiList || filteredDiList.length === 0)
    return <center>No Delivery Instruction found for this Sales Order.</center>

  return (
    <>
      <CardBody className='p-4'>
        <h5 className='mb-4'>Delivery Instructions</h5>
        <Accordion open={openDI} toggle={toggleDI}>
          {filteredDiList.map((di, idx) => {
            const pricingMap = new Map()
            if (Array.isArray(di?.salesOrder?.lineItems)) {
              di.salesOrder.lineItems.forEach(li => {
                pricingMap.set(String(li.model), li)
              })
            }
            const lineItems = Array.isArray(di?.lineItems) ? di.lineItems : []
            const isEditing = String(editingDiId) === String(di._id)
            const diIdStr = String(di._id)
            const statusLower = (di?.status || '').toLowerCase()
            const isApproved = statusLower === 'approved'
            return (
              <AccordionItem key={di._id || idx}>
                <AccordionHeader targetId={di._id || String(idx)}>
                  <div className='d-flex justify-content-between align-items-center w-100'>
                    <span>
                      {`${formatDate(di?.createdAt)}`}
                      {di?.status && (
                        <>
                          {' | '}
                          <span
                            className={
                              di.status.toLowerCase().includes('reject') ||
                                di.status.toLowerCase().includes('deleted')
                                ? 'text-danger'
                                : 'text-success'
                            }
                          >
                            {di?.status === 'deleted'
                              ? 'CANCELLED'
                              : di.status.toUpperCase()}
                          </span>
                          {' | '}
                          <span
                            className={
                              di.status.toLowerCase().includes('reject') ||
                                di.status.toLowerCase().includes('deleted')
                                ? 'text-danger'
                                : 'text-success'
                            }
                          >
                            {di?.approvedOrRejectedBy?.name.toUpperCase()}
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </AccordionHeader>
                <AccordionBody accordionId={di._id || String(idx)}>
                  {String(showPackListDiId) === diIdStr ? (
                    // PackList replaces the DI details for this DI only
                    <PackList
                      diId={di._id}
                      diData={di}
                      onBack={() => setShowPackListDiId(null)}
                    />
                  ) : (
                    <>
                      <Row className='mb-3'>
                        {di?.status === 'rejected' ? (
                          <Col md={12}>
                            <center className='text-danger'>
                              {di?.reasonForRejection}
                            </center>
                          </Col>
                        ) : (
                          ''
                        )}

                        <Col md={4}>
                          <label className='form-label'>
                            <strong>Sales Order Number</strong>
                          </label>
                          <p>
                            {di?.salesOrderNumber ||
                              di?.salesOrder?.salesOrderNumber ||
                              '-'}
                          </p>
                        </Col>

                        <Col md={4}>
                          <label className='form-label'>
                            <strong>DI Number</strong>
                          </label>
                          <p>{di?.deliveryInstructionId || '-'}</p>
                        </Col>

                        <Col md={4}>
                          <label className='form-label'>
                            <strong>Customer PO Number</strong>
                          </label>
                          <p>
                            {di?.customerPONumber ||
                              di?.salesOrder?.customerPONumber ||
                              '-'}
                          </p>
                        </Col>

                        <Col md={4}>
                          <label className='form-label'>
                            <strong>Date of DI</strong>
                          </label>
                          <p>
                            {formatDate(
                              di?.createdAt || di?.salesOrder?.createdAt
                            )}
                          </p>
                        </Col>
                      </Row>

                      <div className='avoid-page-break'>
                        <Table className='so-table' responsive>
                          <thead>
                            <tr>
                              <th>SI</th>
                              <th>Name</th>
                              <th>Part Code</th>
                              <th>HSN</th>
                              <th>UOM</th>
                              <th>QTY</th>
                              <th>LP</th>
                              <th>Discount</th>
                              <th>Rate Per Unit</th>
                              <th>Order Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineItems.length > 0 ? (
                              lineItems.map((data, index) => {
                                const origQty = Number(data?.quantity ?? 0)
                                const editIndex = isEditing
                                  ? editedLineItems.findIndex(
                                    (_, i) => i === index
                                  )
                                  : -1
                                const displayQty =
                                  isEditing && editIndex > -1
                                    ? Number(
                                      editedLineItems[editIndex]?.quantity ??
                                      0
                                    )
                                    : origQty
                                const modelId =
                                  data?.model?._id ??
                                  (typeof data?.model === 'string'
                                    ? data.model
                                    : null)
                                const pricing = modelId
                                  ? pricingMap.get(String(modelId))
                                  : null
                                const listPrice = Number(
                                  pricing?.listPrice ??
                                  data?.model?.listPrice ??
                                  data?.listPrice ??
                                  0
                                )
                                const discount = Number(
                                  pricing?.discount ??
                                  data?.discount ??
                                  data?.model?.discount ??
                                  0
                                )
                                const rate = Number(
                                  pricing?.rate ??
                                  data?.rate ??
                                  listPrice * (1 - discount / 100) ??
                                  0
                                )
                                const value = rate * displayQty
                                return (
                                  <tr key={data?._id ?? index}>
                                    <td>{index + 1}</td>
                                    <td>{getModelName(data)}</td>
                                    <td>{getPartCode(data)}</td>
                                    <td>{data?.model?.HSNNumber}</td>
                                    <td>
                                      {data?.model?.uom ?? data?.uom ?? '-'}
                                    </td>
                                    <td>
                                      {isEditing ? (
                                        <input
                                          type='number'
                                          min='0'
                                          className='form-control'
                                          value={displayQty}
                                          onChange={e =>
                                            onEditQtyChange(
                                              index,
                                              e.target.value
                                            )
                                          }
                                        />
                                      ) : (
                                        displayQty
                                      )}
                                    </td>
                                    <td>{Number(listPrice || 0).toFixed(2)}</td>
                                    <td>{Number(discount || 0).toFixed(2)}%</td>
                                    <td>{Number(rate || 0).toFixed(2)}</td>
                                    <td>{Number(value || 0).toFixed(2)}</td>
                                  </tr>
                                )
                              })
                            ) : (
                              <tr>
                                <td colSpan='9' className='text-center py-4'>
                                  No line items found
                                </td>
                              </tr>
                            )}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td
                                colSpan={9}
                                className='text-end fw-bold'
                              >
                                Total Order Value
                              </td>
                              <td className='fw-bold'>
                                {(
                                  lineItems.reduce((acc, data, i) => {
                                    const modelId =
                                      data?.model?._id ??
                                      (typeof data?.model === 'string' ? data.model : null)
                                    const pricing = modelId ? pricingMap.get(String(modelId)) : null
                                    const rate = Number(
                                      pricing?.rate ??
                                      data?.rate ??
                                      0
                                    )
                                    const editIndex = isEditing
                                      ? editedLineItems.findIndex((_, idx) => idx === i)
                                      : -1
                                    const qty =
                                      isEditing && editIndex > -1
                                        ? Number(editedLineItems[editIndex]?.quantity ?? 0)
                                        : Number(data?.quantity ?? 0)
                                    return acc + rate * qty
                                  }, 0)
                                ).toFixed(2)}
                              </td>
                            </tr>
                          </tfoot>
                        </Table>
                      </div>

                      {di?.remarks && (
                        <Row className='mt-3'>
                          <Col md={12}>
                            <label className='form-label'>
                              <strong>Remarks</strong>
                            </label>
                            <p>{di.remarks}</p>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                  <Row className='mt-3'>
                    <Col className='d-flex justify-content-start'>
                      {userDetails?.role === 'commercial_executive' &&
                        statusLower === 'rejected' && (
                          <>
                            {!isEditing ? (
                              <Button
                                color='warning'
                                outline
                                disabled={submitting}
                                onClick={() => startEdit(di)}
                              >
                                Edit
                              </Button>
                            ) : (
                              <>
                                <Button
                                  color='primary'
                                  className='me-2'
                                  disabled={submitting}
                                  onClick={() => saveEdit(di)}
                                >
                                  Save
                                </Button>
                                <Button
                                  color='secondary'
                                  outline
                                  disabled={submitting}
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </Button>
                              </>
                            )}
                          </>
                        )}
                    </Col>

                    <Col className='d-flex justify-content-end'>
                      {userDetails?.role === 'commercial_manager' && (
                        <>
                          {statusLower === 'approved' ? (
                            <>
                              {/* Cancel button to the left of Create Pack List / Back */}
                              {String(showPackListDiId) === diIdStr ? (
                                <>
                                  <Button
                                    color='danger'
                                    outline
                                    className='me-2'
                                    disabled={submitting}
                                    onClick={() => openDeleteModal(di._id)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    color='success'
                                    className='me-2'
                                    disabled={submitting}
                                    onClick={() => setShowPackListDiId(null)}
                                  >
                                    Back
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    color='danger'
                                    outline
                                    className='me-2'
                                    disabled={submitting}
                                    onClick={() => openDeleteModal(di._id)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    color='success'
                                    className='me-2'
                                    disabled={submitting}
                                    onClick={() => setShowPackListDiId(di._id)}
                                  >
                                    Create Pack List
                                  </Button>
                                </>
                              )}
                            </>
                          ) : statusLower === 'pending' ? (
                            <>
                              <Button
                                color='success'
                                className='me-2'
                                disabled={submitting}
                                onClick={() =>
                                  changeDIStatus(di._id, 'approved', null)
                                }
                              >
                                Approve
                              </Button>
                              <Button
                                color='danger'
                                outline
                                disabled={submitting}
                                onClick={() => openDeclineModal(di._id)}
                              >
                                Decline
                              </Button>
                            </>
                          ) : (
                            ''
                          )}
                        </>
                      )}
                    </Col>
                  </Row>
                </AccordionBody>
              </AccordionItem>
            )
          })}
        </Accordion>
      </CardBody>

      {/* Decline modal */}
      <Modal isOpen={declineModalOpen} toggle={closeDeclineModal}>
        <ModalHeader toggle={closeDeclineModal}>
          Reason for Rejection
        </ModalHeader>
        <ModalBody>
          <Label for='reason'>Please provide reason for rejection</Label>
          <Input
            id='reason'
            type='textarea'
            value={declineReason}
            onChange={e => setDeclineReason(e.target.value)}
            placeholder='Type reason for rejection...'
            rows={4}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            onClick={closeDeclineModal}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            color='danger'
            onClick={() =>
              restoreSOQuantitiesAndRejectDI(activeDiId, declineReason)
            }
            disabled={submitting}
          >
            Reject
          </Button>
        </ModalFooter>
      </Modal>

      {/* Soft-delete (mark deleted) confirmation modal */}
      <Modal isOpen={deleteModalOpen} toggle={closeDeleteModal}>
        <ModalHeader toggle={closeDeleteModal}>
          Cancel Delivery Instruction
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to cancel this Delivery Instruction? This will
            also delete respective Pack Lists (if created).
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            onClick={closeDeleteModal}
            disabled={submitting}
          >
            No, keep DI
          </Button>
          <Button
            color='danger'
            onClick={markDIAsDeleted}
            disabled={submitting}
          >
            Yes, Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ViewDI
